/**
 * App — Portal v2 provider stack (DEC-063).
 *
 * AppearanceProvider → brand-native MUI theme (rebuilt on density/mode change)
 * → CssBaseline → NotificationProvider → ErrorBoundary → AuthProvider → routes.
 * Brand OKLCH tokens are injected on the document root at mount.
 */

import React, { Suspense, useEffect, useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { AppRoutes } from './router';
import { AuthProvider } from './auth/AuthProvider';
import { installAuthInterceptor } from './auth/authClient';
import { SessionExpiredHandler } from './auth/SessionExpiredHandler';
import { NotificationProvider } from './contexts/NotificationContext';
import { AppearanceProvider, useAppearance } from './theme/AppearanceContext';
import { createPortalTheme, injectBrandTokens } from './theme/portalTheme';
import { PORTAL_META } from './registry/portalMeta';
import { ForgeProvider, ForgeFloatingButton } from '@design2code/forge-ui';
import { PSCWidget } from '@design2code/psc-widget';
import '@design2code/psc-widget/styles';

// Refresh-on-401 for every axios consumer (deduped default instance)
installAuthInterceptor();

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Application error:', error, info);
  }
  override render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            p: 3,
            textAlign: 'center',
          }}
        >
          <h1>Something went wrong</h1>
          <p style={{ maxWidth: 500 }}>An unexpected error occurred. Please refresh the page.</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '8px 16px', cursor: 'pointer' }}>
            Refresh Page
          </button>
        </Box>
      );
    }
    return this.props.children;
  }
}

const ThemedApp: React.FC = () => {
  const { density, resolvedMode } = useAppearance();
  const theme = useMemo(() => createPortalTheme(density, resolvedMode), [density, resolvedMode]);

  useEffect(() => {
    injectBrandTokens();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <ErrorBoundary>
          <AuthProvider>
            <SessionExpiredHandler />
            <ForgeProvider serviceId={PORTAL_META.platformName} forgeApiUrl="/api/v1/forge">
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
                    <CircularProgress />
                  </Box>
                }
              >
                <AppRoutes />
              </Suspense>
              <ForgeFloatingButton position="bottom-right" serviceId={PORTAL_META.platformName} />
              <PSCWidget
                config={{ appName: PORTAL_META.platformTitle, apiBaseUrl: '/api/v1/forge', position: 'bottom-left' }}
              />
            </ForgeProvider>
          </AuthProvider>
        </ErrorBoundary>
      </NotificationProvider>
    </ThemeProvider>
  );
};

const App: React.FC = () => (
  <AppearanceProvider>
    <ThemedApp />
  </AppearanceProvider>
);

export default App;
